import { useCallback, useEffect, useRef, useState } from 'react';
import { adjustTimeByMinuteStep, to12Hour, to24Hour } from './utils';

type Period = 'AM' | 'PM';

type UseSegmentedTimeInputOptions = {
  value: string; // "HH:mm" 24h format
  onChange: (value: string) => void;
  is24Hour?: boolean;
  step?: number;
};

export const useSegmentedTimeInput = ({
  value,
  onChange,
  is24Hour = true,
  step = 15,
}: UseSegmentedTimeInputOptions) => {
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const periodRef = useRef<HTMLButtonElement>(null);
  const hourTextRef = useRef('');
  const minuteTextRef = useRef('');
  const periodRefValue = useRef<Period>('AM');

  // Ref guard to prevent circular updates when external value changes
  const lastInternalValueRef = useRef<string | null>(null);

  const parseExternalValue = useCallback(
    (val: string): { hourText: string; minuteText: string; period: Period } => {
      if (!val) return { hourText: '', minuteText: '', period: 'AM' };
      const [hStr, mStr] = val.split(':');
      const h = parseInt(hStr ?? '', 10);
      const m = parseInt(mStr ?? '', 10);
      if (isNaN(h) || isNaN(m)) return { hourText: '', minuteText: '', period: 'AM' };

      if (is24Hour) {
        return {
          hourText: String(h).padStart(2, '0'),
          minuteText: String(m).padStart(2, '0'),
          period: 'AM',
        };
      }
      const { hour12, period } = to12Hour(h);
      return {
        hourText: String(hour12),
        minuteText: String(m).padStart(2, '0'),
        period,
      };
    },
    [is24Hour],
  );

  const initial = parseExternalValue(value);
  const [hourText, setHourText] = useState(initial.hourText);
  const [minuteText, setMinuteText] = useState(initial.minuteText);
  const [period, setPeriod] = useState<Period>(initial.period);

  useEffect(() => {
    hourTextRef.current = hourText;
  }, [hourText]);

  useEffect(() => {
    minuteTextRef.current = minuteText;
  }, [minuteText]);

  useEffect(() => {
    periodRefValue.current = period;
  }, [period]);

  // Sync from external value changes (e.g. popover selection)
  useEffect(() => {
    if (lastInternalValueRef.current === value) {
      lastInternalValueRef.current = null;
      return;
    }

    lastInternalValueRef.current = null;

    const parsed = parseExternalValue(value);
    setHourText(parsed.hourText);
    setMinuteText(parsed.minuteText);
    setPeriod(parsed.period);
  }, [value, parseExternalValue]);

  const compose = useCallback(
    (h: string, m: string, p: Period) => {
      const hNum = parseInt(h, 10);
      if (isNaN(hNum)) return;

      const mNum = parseInt(m, 10);
      const minute = isNaN(mNum) ? 0 : mNum;

      let hour24: number;
      if (is24Hour) {
        hour24 = hNum;
      } else {
        if (hNum < 1 || hNum > 12) return;
        hour24 = to24Hour(hNum, p);
      }

      const composed = `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      lastInternalValueRef.current = composed;
      onChange(composed);
    },
    [is24Hour, onChange],
  );

  const setFromValue = useCallback(
    (nextValue: string) => {
      const parsed = parseExternalValue(nextValue);
      setHourText(parsed.hourText);
      setMinuteText(parsed.minuteText);
      setPeriod(parsed.period);
      lastInternalValueRef.current = nextValue;
      onChange(nextValue);
    },
    [onChange, parseExternalValue],
  );

  const getComposedValue = useCallback(() => {
    const currentHourText = hourTextRef.current;
    if (!currentHourText) return '';

    const hourNum = parseInt(currentHourText, 10);
    if (isNaN(hourNum)) return '';

    const minuteNum = parseInt(minuteTextRef.current, 10);
    const minute = isNaN(minuteNum) ? 0 : minuteNum;

    let hour24: number;
    if (is24Hour) {
      hour24 = hourNum;
    } else {
      if (hourNum < 1 || hourNum > 12) return '';
      hour24 = to24Hour(hourNum, periodRefValue.current);
    }

    return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }, [is24Hour]);

  const maxHour = is24Hour ? 23 : 12;
  const minHour = is24Hour ? 0 : 1;

  const handleHourChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, '');
      if (raw === '') {
        setHourText('');
        return;
      }

      const num = parseInt(raw, 10);
      if (num > maxHour || (raw.length >= 2 && num < minHour)) {
        // Reset DOM value — React skips re-render when state hasn't changed
        e.target.value = hourText;
        return;
      }

      // Auto-advance: if 2 digits entered, or single digit that can't grow
      const shouldAdvance = raw.length >= 2 || (raw.length === 1 && num * 10 > maxHour);
      const text = is24Hour && shouldAdvance ? String(num).padStart(2, '0') : String(num);
      setHourText(text);

      if (shouldAdvance) {
        minuteRef.current?.focus();
        minuteRef.current?.select();
      }

      // Compose partial value (hour:00) for popover auto-scroll
      compose(text, minuteText, period);
    },
    [hourText, maxHour, minHour, minuteText, period, compose],
  );

  const handleMinuteChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, '');
      if (raw === '') {
        setMinuteText('');
        return;
      }

      const num = parseInt(raw, 10);
      if (num > 59) {
        e.target.value = minuteText;
        return;
      }

      const text = raw.length >= 2 ? String(num).padStart(2, '0') : raw;
      setMinuteText(text);

      if (raw.length >= 2) {
        setMinuteText(String(num).padStart(2, '0'));
      }

      compose(hourText, text, period);
    },
    [compose, hourText, period, minuteText],
  );

  const handleHourKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (/^\d$/.test(e.key)) {
        const input = e.currentTarget;
        const { value: inputValue, selectionStart, selectionEnd } = input;
        // Intercept when: field is full (maxLength blocks insertion), OR field has
        // 1 char with cursor at pos 0 (browser would insert + auto-advance via onChange).
        const isCursorPositioned = selectionStart === selectionEnd;
        const isFullField = inputValue.length >= 2;
        const isSingleCharAtStart = inputValue.length === 1 && selectionStart === 0;
        if (isCursorPositioned && (isFullField || isSingleCharAtStart)) {
          e.preventDefault();
          if (selectionStart === 0) {
            // Cursor before first digit: replace first digit, keep second
            // For a 1-char field, the "second" char is inputValue[0] (the only existing char)
            const secondChar = isFullField ? (inputValue[1] ?? '') : (inputValue[0] ?? '');
            const combined = parseInt(e.key + secondChar, 10);
            if (isNaN(combined) || combined > maxHour || combined < minHour) {
              // Composed value invalid — fall back to fresh entry with just the typed digit
              const num = parseInt(e.key, 10);
              if (num > maxHour || (num < minHour && num * 10 < minHour)) return;
              const text = String(num);
              setHourText(text);
              if (num * 10 > maxHour) {
                minuteRef.current?.focus();
                minuteRef.current?.select();
              }
              compose(text, minuteText, period);
              return;
            }
            const text = is24Hour ? String(combined).padStart(2, '0') : String(combined);
            setHourText(text);
            // Stay in hour field with cursor after the first digit
            requestAnimationFrame(() => {
              hourRef.current?.setSelectionRange(1, 1);
            });
            compose(text, minuteText, period);
          } else if (selectionStart === 1) {
            // Cursor between digits: replace second digit, keep first
            const combined = parseInt((inputValue[0] ?? '') + e.key, 10);
            if (isNaN(combined) || combined > maxHour || combined < minHour) return;
            const text = is24Hour ? String(combined).padStart(2, '0') : String(combined);
            setHourText(text);
            minuteRef.current?.focus();
            minuteRef.current?.select();
            compose(text, minuteText, period);
          } else {
            // Cursor at end: start fresh entry
            const num = parseInt(e.key, 10);
            if (num > maxHour || (num < minHour && num * 10 < minHour)) return;
            const text = String(num);
            setHourText(text);
            if (num * 10 > maxHour) {
              minuteRef.current?.focus();
              minuteRef.current?.select();
            }
            compose(text, minuteText, period);
          }
        }
        return;
      }
      if (e.key === 'ArrowRight') {
        const input = e.currentTarget;
        if (input.selectionStart === input.value.length) {
          e.preventDefault();
          minuteRef.current?.focus();
          minuteRef.current?.setSelectionRange(0, 0);
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const num = parseInt(hourText, 10);
        const current = isNaN(num) ? minHour : num;
        let next: number;
        if (e.key === 'ArrowUp') {
          next = current >= maxHour ? minHour : current + 1;
        } else {
          next = current <= minHour ? maxHour : current - 1;
        }
        const padded = is24Hour ? String(next).padStart(2, '0') : String(next);
        setHourText(padded);
        compose(padded, minuteText, period);
      }
    },
    [hourText, minuteText, period, compose, maxHour, minHour],
  );

  const handleMinuteKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (/^\d$/.test(e.key)) {
        const input = e.currentTarget;
        const { value: inputValue, selectionStart, selectionEnd } = input;
        if (inputValue.length >= 2 && selectionStart === selectionEnd) {
          e.preventDefault();
          if (selectionStart === 0) {
            // Cursor before first digit: replace first digit, keep second
            const combined = parseInt(e.key + (inputValue[1] ?? ''), 10);
            if (isNaN(combined) || combined > 59) {
              // Composed value invalid — fall back to fresh entry
              const num = parseInt(e.key, 10);
              if (num > 5) return;
              const text = String(num);
              setMinuteText(text);
              compose(hourText, text, period);
              return;
            }
            const text = String(combined).padStart(2, '0');
            setMinuteText(text);
            // Stay in minute field, cursor after first digit
            requestAnimationFrame(() => {
              minuteRef.current?.setSelectionRange(1, 1);
            });
            compose(hourText, text, period);
          } else if (selectionStart === 1) {
            // Cursor between digits: replace second digit, keep first
            const combined = parseInt((inputValue[0] ?? '') + e.key, 10);
            if (isNaN(combined) || combined > 59) return;
            const text = String(combined).padStart(2, '0');
            setMinuteText(text);
            compose(hourText, text, period);
          } else {
            // Cursor at end: start fresh entry
            const num = parseInt(e.key, 10);
            if (num > 5) return;
            const text = String(num);
            setMinuteText(text);
            compose(hourText, text, period);
          }
        }
        return;
      }
      if (e.key === 'Backspace' && e.currentTarget.value === '') {
        e.preventDefault();
        hourRef.current?.focus();
        hourRef.current?.select();
      } else if (e.key === 'ArrowLeft') {
        const input = e.currentTarget;
        if (input.selectionStart === 0) {
          e.preventDefault();
          hourRef.current?.focus();
          const len = hourRef.current?.value.length ?? 0;
          hourRef.current?.setSelectionRange(len, len);
        }
      } else if (e.key === 'ArrowRight' && !is24Hour) {
        const input = e.currentTarget;
        if (input.selectionStart === input.value.length) {
          e.preventDefault();
          periodRef.current?.focus();
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const hourNum = parseInt(hourText, 10);
        const minuteNum = parseInt(minuteText, 10);

        let currentValue = value;

        if (!isNaN(hourNum)) {
          const hour24 = is24Hour ? hourNum : to24Hour(hourNum, period);
          const minute = isNaN(minuteNum) ? 0 : minuteNum;
          currentValue = `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }

        const nextValue = adjustTimeByMinuteStep(
          currentValue,
          step,
          e.key === 'ArrowUp' ? 'up' : 'down',
        );

        if (!nextValue) return;

        setFromValue(nextValue);
      }
    },
    [hourText, is24Hour, minuteText, period, setFromValue, step, value],
  );

  const togglePeriod = useCallback(() => {
    const next: Period = period === 'AM' ? 'PM' : 'AM';
    setPeriod(next);
    compose(hourText, minuteText, next);
  }, [hourText, minuteText, period, compose]);

  const handlePeriodKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        togglePeriod();
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault();
        minuteRef.current?.focus();
        const len = minuteRef.current?.value.length ?? 0;
        minuteRef.current?.setSelectionRange(len, len);
      }
    },
    [togglePeriod],
  );

  const clear = useCallback(() => {
    setHourText('');
    setMinuteText('');
    setPeriod('AM');
  }, []);

  return {
    hourText,
    minuteText,
    period,
    hourRef,
    minuteRef,
    periodRef,
    clear,
    getComposedValue,
    setFromValue,
    togglePeriod,
    hourProps: {
      value: hourText,
      onChange: handleHourChange,
      onKeyDown: handleHourKeyDown,
    },
    minuteProps: {
      value: minuteText,
      onChange: handleMinuteChange,
      onKeyDown: handleMinuteKeyDown,
    },
    periodProps: {
      onKeyDown: handlePeriodKeyDown,
    },
  };
};
